import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";

export default function Terms() {
  const { lang } = useLang();
  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="terms-page">
      <header className="px-6 md:px-12 lg:px-16 py-6 border-b border-[var(--border)]">
        <Link to="/" className="label-cap" data-testid="terms-back-home">— Mordomo</Link>
      </header>
      <article className="px-6 md:px-12 lg:px-16 py-12 md:py-20 max-w-3xl">
        <p className="label-cap">— {lang === "pt" ? "Documento legal" : "Legal"}</p>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="terms-title">
          {lang === "pt" ? "Termos de Serviço" : "Terms of Service"}
        </h1>
        <p className="font-display-italic text-[var(--ink-soft)] mt-3">
          {lang === "pt" ? "Versão de 17 de Junho de 2026" : "Version dated June 17, 2026"}
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed">
          {lang === "pt" ? <ContentPT /> : <ContentEN />}
        </div>

        <div className="mt-16 pt-6 border-t border-[var(--border)] flex gap-6 text-sm">
          <Link to="/privacy" className="label-cap hover:text-[var(--oxblood)]" data-testid="terms-to-privacy">
            {lang === "pt" ? "Política de Privacidade" : "Privacy Policy"}
          </Link>
          <Link to="/" className="label-cap hover:text-[var(--oxblood)]" data-testid="terms-to-home">
            {lang === "pt" ? "Voltar à página inicial" : "Back to home"}
          </Link>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-light mt-8 mb-3 text-[var(--ink)]">{title}</h2>
      {children}
    </section>
  );
}

function ContentPT() {
  return (
    <>
      <Section title="1. Aceitação">
        <p>Ao criar uma conta no Mordomo aceita os presentes Termos. Se não concorda, basta não usar o serviço. A subscrição implica a leitura e aceitação destes Termos e da Política de Privacidade.</p>
      </Section>
      <Section title="2. O que é o Mordomo">
        <p>Um serviço de chefe-de-gabinete pessoal entregue por WhatsApp e por uma aplicação web. Ajudamos mulheres ocupadas a organizar logística, casa, finanças, educação dos filhos, reinvenção profissional. Não somos um serviço médico, jurídico, financeiro ou psicológico — para esses assuntos, consulte profissionais qualificados.</p>
      </Section>
      <Section title="3. Subscrição e pagamento">
        <p>O plano único custa $29.99/mês, com 26 dias de teste grátis. O pagamento é processado pela Lemon Squeezy ou PayPal. Pode cancelar a qualquer momento — o acesso mantém-se até ao fim do ciclo pago. Não fazemos reembolsos parciais por ciclos já iniciados, mas em casos pontuais avaliamos caso a caso, com bom senso.</p>
      </Section>
      <Section title="4. Uso aceitável">
        <p>O Mordomo é seu, mas pedimos algumas cortesias:</p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>Não tente extrair, copiar ou redistribuir o nosso modelo, prompts, ou conteúdo gerado.</li>
          <li>Não use o serviço para fins ilegais, fraudulentos ou ofensivos.</li>
          <li>Não tente piratear ou sobrecarregar os nossos servidores.</li>
          <li>Use os números de telefone que tem direito a usar — em particular, só registe os WhatsApp dos seus filhos com o consentimento deles (acima dos 16 anos a lei requer consentimento informado em Portugal).</li>
        </ul>
      </Section>
      <Section title="5. Inteligência artificial">
        <p>As respostas do Mordomo são geradas por modelos de IA (Claude Sonnet 4.5 e outros). Apesar do nosso cuidado editorial, podem ocasionalmente conter imprecisões. Use o seu discernimento: o Mordomo apoia, não substitui, a sua decisão. Em assuntos sensíveis — saúde, dinheiro, contratos — consulte profissionais.</p>
      </Section>
      <Section title="6. Acordos digitais">
        <p>Os acordos parentais que cria com os seus filhos no Mordomo são um instrumento educativo, não um contrato vinculativo. Servem para alinhar conversas em família, não para substituir o papel da mãe ou do pai.</p>
      </Section>
      <Section title="7. Propriedade intelectual">
        <p>O código, design, prompts e marca do Mordomo são nossos. O conteúdo que você partilha (mensagens, fotos da Memória, anotações) é seu — não reclamamos direitos sobre ele. Concede-nos apenas a licença mínima necessária para operar o serviço.</p>
      </Section>
      <Section title="8. Suspensão e cancelamento">
        <p>Podemos suspender ou encerrar uma conta que viole estes Termos, com aviso prévio quando possível. Você pode cancelar a qualquer momento no painel de subscrição. Os seus dados são tratados conforme a Política de Privacidade.</p>
      </Section>
      <Section title="9. Limitação de responsabilidade">
        <p>O Mordomo é fornecido «tal como está». Não somos responsáveis por danos indirectos, lucros cessantes, ou perdas resultantes de decisões que tome com base em conteúdo gerado pelo serviço. A nossa responsabilidade máxima é limitada ao valor pago pela Chefe nos 12 meses anteriores ao incidente.</p>
      </Section>
      <Section title="10. Lei aplicável e foro">
        <p>Estes Termos são regidos pela lei portuguesa. Litígios são da competência do Tribunal Judicial da Comarca de Lisboa. Antes de qualquer acção judicial, comprometemo-nos a tentar resolver questões por mediação informal — escreva-nos para <a href="mailto:legal@my-butler.ai" className="text-[var(--oxblood)] underline">legal@my-butler.ai</a>.</p>
      </Section>
    </>
  );
}

function ContentEN() {
  return (
    <>
      <Section title="1. Acceptance">
        <p>Creating an account means accepting these Terms and the Privacy Policy.</p>
      </Section>
      <Section title="2. What the Mordomo is">
        <p>A personal chief-of-staff service via WhatsApp and web. Not medical, legal, or financial advice.</p>
      </Section>
      <Section title="3. Subscription & payment">
        <p>Single tier at $29.99/month with 26-day free trial. Processed by Lemon Squeezy or PayPal. Cancel anytime; access continues to cycle end.</p>
      </Section>
      <Section title="4. Acceptable use">
        <p>Do not extract our prompts, do not pirate, do not use for illegal purposes, register children's phones only with their informed consent.</p>
      </Section>
      <Section title="5. AI">
        <p>Responses are AI-generated and may occasionally err. The Mordomo supports, does not replace, your judgment.</p>
      </Section>
      <Section title="6. Digital agreements">
        <p>Parental pacts are an educational tool, not a binding contract.</p>
      </Section>
      <Section title="7. IP">
        <p>Our code/design/prompts/brand belong to us. Your content (messages, memory captures) belongs to you.</p>
      </Section>
      <Section title="8. Suspension">
        <p>We may suspend accounts that violate these Terms. You may cancel anytime.</p>
      </Section>
      <Section title="9. Liability">
        <p>Provided «as is». Liability capped at the last 12 months of fees paid.</p>
      </Section>
      <Section title="10. Governing law">
        <p>Portuguese law. Court of Lisbon. Pre-litigation mediation: <a href="mailto:legal@my-butler.ai" className="text-[var(--oxblood)] underline">legal@my-butler.ai</a>.</p>
      </Section>
    </>
  );
}
